/**
 * AppStructureMap — the "Mappa" builder view (app-structure map, Slice 1).
 *
 * A read-first, layered graph of the WHOLE app in three lanes that read
 * left→right (who consumes → how → what):
 *   INTERFACCE (pages · forms · chat/voce)  →  LOGICA/QUERY (AppQueries)  →
 *   DATI (tables · datasets).
 *
 * It answers the questions no single editor does — "chi legge/scrive questa
 * tabella?", "da cosa dipende questa pagina?", "cosa è orfano?" — and it's the
 * navigation HUB tying Core and Interfacce together: clicking a node jumps into
 * the right editor (table→Core, page→Interfacce, Query→dock).
 *
 * Anti-clutter (the owner's concern at scale): nodes are COLLAPSED (title only)
 * and the primary interaction is **Focus mode** — click a node and everything
 * but its 1-hop neighbourhood dims out. On top of that: toolbar FILTERS that HIDE
 * (search · "solo problemi" · lane toggles, Slice 2) and — since the web-app pages
 * would otherwise dwarf every other interface — the pages start FOLDED into one
 * "Web app" group node, expandable on demand (Slice 3). Matrix view is a later slice.
 *
 * Everything is derived CLIENT-SIDE (no new backend endpoint) from data the
 * builder already exposes. `buildAppStructureGraph` is the PURE derivation
 * (unit-tested); the component is the ReactFlow shell around it (deferred test
 * tier, like AppErDiagram). Reuses `AppErDiagram`'s column-kind helpers.
 */
import { useQueries, useQuery } from "@tanstack/react-query";
import { ChevronRight, Database, FileText, Globe, Layers, MessageSquare, Search, Server, Table2, Webhook } from "lucide-react";
import { memo, useEffect, useState } from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from "reactflow";
import "reactflow/dist/style.css";

import { AppErDiagram } from "@/components/apps/AppErDiagram";
import { appsApi, type ColumnOut } from "@/lib/api-apps";
import {
  applyMapFilters,
  buildAppStructureGraph,
  buildMatrix,
  interfaceScopeGraph,
  overviewGraph,
  type AppMatrix,
  type EdgeTone,
  type Lane,
  type MapNodeKind,
  type MapView,
  type StructureGraph,
  type StructureNode,
} from "@/components/apps/appStructureGraph";

// ── Lanes (left→right) + geometry ─────────────────────────────────────────────
const LANE_X: Record<Lane, number> = { iface: 0, query: 360, data: 760 };
const LANE_W: Record<Lane, number> = { iface: 240, query: 220, data: 250 };
const ROW_H = 66;
const ROW_TOP = 40;

// ── ReactFlow node ────────────────────────────────────────────────────────────
interface MapNodeData extends Omit<StructureNode, "lane"> {
  dim?: boolean;
  drillable?: boolean;
}
const KIND_ICON: Record<MapNodeKind, typeof Globe> = {
  page: Globe,
  form: FileText,
  assistant: MessageSquare,
  rest: Webhook,
  webapp: Layers,
  query: Server,
  logic: Server,
  table: Table2,
  dataset: Database,
};
const laneOfKind = (k: MapNodeKind): Lane =>
  k === "query" || k === "logic" ? "query" : k === "table" || k === "dataset" ? "data" : "iface";

const MapNode = memo(({ data }: NodeProps<MapNodeData>) => {
  const Icon = KIND_ICON[data.kind];
  const write = data.badge?.tone === "write";
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border bg-card px-2.5 py-2 shadow-sm ${data.orphan ? "border-danger/60" : data.drillable ? "border-accent/50 cursor-pointer hover:border-accent" : "border-border"}`}
      style={{ width: LANE_W[laneOfKind(data.kind)], opacity: data.dim ? 0.16 : 1 }}
      title={data.drillable ? `Entra in ${data.label}` : data.sub ? `${data.label} — ${data.sub}` : data.label}
    >
      <Handle type="target" position={Position.Left} className="!h-1.5 !w-1.5 !border-0 !bg-border" />
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
          data.kind === "dataset" ? "bg-muted text-muted-foreground" : "bg-accent/10 text-accent"
        }`}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-xs font-semibold text-foreground">{data.label}</span>
        {data.sub && <span className="truncate text-[10px] text-muted-foreground">{data.sub}</span>}
      </div>
      {data.badge && !data.orphan && (
        <span
          className={`ml-auto shrink-0 rounded px-1 py-0.5 text-[8.5px] font-bold ${write ? "text-warning" : "text-accent"}`}
          style={{ border: `1px solid ${write ? "#f59e0b" : "hsl(var(--accent))"}` }}
        >
          {data.badge.text}
        </span>
      )}
      {data.orphan && (
        <span className="ml-auto shrink-0 rounded px-1 py-0.5 text-[8.5px] font-bold text-danger" style={{ border: "1px solid hsl(var(--danger))" }}>
          ORFANO
        </span>
      )}
      {data.drillable && <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-accent" strokeWidth={2} />}
      <Handle type="source" position={Position.Right} className="!h-1.5 !w-1.5 !border-0 !bg-border" />
    </div>
  );
});
MapNode.displayName = "MapNode";
const nodeTypes = { map: MapNode };

const EDGE_COLOR: Record<EdgeTone, string> = {
  read: "hsl(var(--accent))",
  write: "#f59e0b",
  rel: "hsl(var(--muted-foreground))",
  dataset: "hsl(var(--muted-foreground))",
};

export interface AppStructureMapProps {
  appId: string;
  /** operator has apps:write on this app — enables the editable ER in the Data view */
  canWrite?: boolean;
  onOpenTable?: (id: string) => void;
  onOpenDataset?: (id: string) => void;
  onOpenQuery?: (id: string) => void;
  onOpenPage?: (id: string) => void;
  onOpenForm?: (id: string) => void;
}

export function AppStructureMap(props: AppStructureMapProps) {
  const { appId } = props;
  const [focusId, setFocusId] = useState<string | null>(null);
  // Slice 4 — the map is a NAVIGATOR: overview → drill into an interface → data on demand.
  const [view, setView] = useState<MapView>({ kind: "overview" });
  // Slice 2 — legibility filters (HIDE, vs Focus mode which DIMS).
  const [query, setQuery] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [hiddenLanes, setHiddenLanes] = useState<Set<Lane>>(new Set());
  const goTo = (v: MapView) => {
    setFocusId(null);
    setView(v);
  };
  const toggleLane = (l: Lane) =>
    setHiddenLanes((s) => {
      const next = new Set(s);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return next;
    });

  const tablesQ = useQuery({ queryKey: ["apps", appId, "tables"], queryFn: () => appsApi.listTables(appId), enabled: !!appId });
  const datasetsQ = useQuery({ queryKey: ["apps", appId, "datasets"], queryFn: () => appsApi.listDatasets(appId), enabled: !!appId });
  const formsQ = useQuery({ queryKey: ["apps", appId, "forms"], queryFn: () => appsApi.listForms(appId), enabled: !!appId });
  const queriesQ = useQuery({ queryKey: ["apps", appId, "queries"], queryFn: () => appsApi.listQueries(appId), enabled: !!appId });
  const pagesQ = useQuery({ queryKey: ["apps", appId, "pages"], queryFn: () => appsApi.listPages(appId), enabled: !!appId });
  const interfacesQ = useQuery({ queryKey: ["apps", appId, "interfaces"], queryFn: () => appsApi.listInterfaces(appId), enabled: !!appId });

  const tableIds = (tablesQ.data ?? []).map((t) => t.id);
  const tableDetailQs = useQueries({
    queries: tableIds.map((tid) => ({
      queryKey: ["apps", appId, "tables", tid],
      queryFn: () => appsApi.getTable(appId, tid),
      enabled: !!appId,
    })),
  });
  const columnsByTable = new Map<string, ColumnOut[]>();
  tableDetailQs.forEach((q) => {
    if (q.data) columnsByTable.set(q.data.id, q.data.columns);
  });

  const fullGraph = buildAppStructureGraph({
    tables: tablesQ.data ?? [],
    columnsByTable,
    datasets: datasetsQ.data ?? [],
    queries: queriesQ.data ?? [],
    pages: pagesQ.data ?? [],
    forms: formsQ.data ?? [],
    interfaces: interfacesQ.data ?? [],
  });
  // The current VIEW's subgraph (overview = interface cards only; drill = scoped
  // to one interface; data = the ER on demand), then filters HIDE within it and
  // Focus mode DIMS within what survives.
  const isOverview = view.kind === "overview";
  const isMatrix = view.kind === "matrix";
  const isData = view.kind === "data";
  const base: StructureGraph =
    view.kind === "overview"
      ? overviewGraph(fullGraph)
      : view.kind === "interface"
        ? interfaceScopeGraph(fullGraph, view.id)
        : { nodes: [], edges: [], adjacency: new Map() }; // data (editable ER) + matrix — own render paths
  const { nodes, edges, adjacency } = applyMapFilters(base, { query, onlyProblems, hiddenLanes });

  // Matrix view: a consumer×data grid instead of a graph. Rows filtered by the same
  // search / "solo problemi" toolbar; columns unfiltered.
  const matrix = ((): AppMatrix | null => {
    if (!isMatrix) return null;
    const m = buildMatrix(fullGraph);
    const q = query.trim().toLowerCase();
    const rows = m.rows.filter((r) => (!onlyProblems || r.orphan) && (!q || r.label.toLowerCase().includes(q)));
    return { columns: m.columns, rows };
  })();

  const enterInterface = (id: string) => {
    const node = base.nodes.find((n) => n.id === id);
    goTo({ kind: "interface", id, label: node?.label ?? "Interfaccia" });
  };

  const openEntity = (kind: MapNodeKind, entityId: string): (() => void) | undefined => {
    if (kind === "table") return () => props.onOpenTable?.(entityId);
    if (kind === "dataset") return () => props.onOpenDataset?.(entityId);
    if (kind === "query" || kind === "logic") return () => props.onOpenQuery?.(entityId);
    if (kind === "page") return () => props.onOpenPage?.(entityId);
    if (kind === "form") return () => props.onOpenForm?.(entityId);
    return undefined;
  };
  const openFor = (n: StructureNode) => openEntity(n.kind, n.entityId);

  const focusSet: Set<string> | null = focusId
    ? new Set<string>([focusId, ...(adjacency.get(focusId) ?? [])])
    : null;

  // Overview lays interface cards in a 2-column grid; every other view keeps the
  // 3-lane left→right flow.
  const perLane: Record<Lane, number> = { iface: 0, query: 0, data: 0 };
  let gridIdx = 0;
  const rfNodes: Node<MapNodeData>[] = nodes.map((n) => {
    let position: { x: number; y: number };
    if (isOverview) {
      const col = gridIdx % 2;
      const row = Math.floor(gridIdx / 2);
      gridIdx++;
      position = { x: 80 + col * 300, y: ROW_TOP + row * 92 };
    } else {
      const row = perLane[n.lane]++;
      position = { x: LANE_X[n.lane], y: ROW_TOP + row * ROW_H };
    }
    return {
      id: n.id,
      type: "map",
      position,
      data: { ...n, dim: focusSet ? !focusSet.has(n.id) : false, drillable: isOverview },
      draggable: true,
    };
  });
  const rfEdges: Edge[] = edges.map((e, i) => {
    const dim = focusSet ? !(focusSet.has(e.from) && focusSet.has(e.to)) : false;
    return {
      id: `e${i}:${e.from}->${e.to}`,
      source: e.from,
      target: e.to,
      markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_COLOR[e.tone], width: 14, height: 14 },
      style: {
        stroke: EDGE_COLOR[e.tone],
        strokeWidth: 1.5,
        strokeDasharray: e.tone === "dataset" ? "4 3" : undefined,
        opacity: dim ? 0.08 : e.tone === "rel" || e.tone === "dataset" ? 0.55 : 0.85,
      },
    };
  });

  const loading =
    tablesQ.isLoading || datasetsQ.isLoading || queriesQ.isLoading || pagesQ.isLoading || formsQ.isLoading || interfacesQ.isLoading;
  const focused = focusId ? nodes.find((n) => n.id === focusId) : null;
  // Summary reflects the FULL app; the toolbar filters what the canvas shows.
  const all = fullGraph.nodes;
  const counts = {
    iface: all.filter((n) => n.lane === "iface").length,
    query: all.filter((n) => n.lane === "query").length,
    data: all.filter((n) => n.lane === "data").length,
  };
  const orphans = all.filter((n) => n.orphan).length;
  const filtering = query.trim() !== "" || onlyProblems || hiddenLanes.size > 0;
  const LANE_LABEL: Record<Lane, string> = { iface: "Interfacce", query: "Query", data: "Dati" };
  // Fit-view animation retriggers whenever the VIEW changes (not on filter tweaks).
  const viewKey = view.kind === "interface" ? `iface:${view.id}` : view.kind;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-surface/40 px-3 py-1.5 text-[11px] text-muted-foreground">
        {/* Breadcrumb — Mappa (overview) ▸ current view. */}
        <button
          type="button"
          onClick={() => goTo({ kind: "overview" })}
          className={`font-semibold ${isOverview ? "text-foreground" : "text-accent hover:underline"}`}
        >
          Mappa
        </button>
        {!isOverview && (
          <>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-semibold text-foreground">
              {view.kind === "data" ? "Dati" : view.kind === "matrix" ? "Matrice" : view.label}
            </span>
          </>
        )}
        <span className="text-muted-foreground/70">· {counts.iface} interfacce · {counts.query} query · {counts.data} dati</span>
        {orphans > 0 && <span className="text-danger">· {orphans} orfani</span>}
        {/* Data view (ER) + Matrix view — both opened on demand from the overview. */}
        {isOverview && counts.data > 0 && (
          <button
            type="button"
            onClick={() => goTo({ kind: "data" })}
            className="rounded border border-fuchsia-500/40 px-1.5 py-0.5 text-fuchsia-300 hover:border-fuchsia-400"
          >
            ◫ Vista Dati
          </button>
        )}
        {isOverview && counts.data > 0 && (
          <button
            type="button"
            onClick={() => goTo({ kind: "matrix" })}
            className="rounded border border-accent/40 px-1.5 py-0.5 text-accent hover:border-accent"
          >
            ▦ Matrice
          </button>
        )}
        {/* Filters — inert in the ER view (AppErDiagram has its own controls). */}
        {!isData && (
          <>
            {/* Search — highlights matches + their neighbours, hides the rest. */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="cerca…"
                className="h-6 w-36 rounded border border-border bg-background pl-6 pr-2 text-[11px] outline-none focus:border-accent"
              />
            </div>
            {/* Solo problemi — the orphan-only backstop. */}
            <button
              type="button"
              onClick={() => setOnlyProblems((v) => !v)}
              className={`rounded border px-1.5 py-0.5 ${onlyProblems ? "border-danger text-danger" : "border-border hover:text-foreground"}`}
            >
              ◍ Solo problemi
            </button>
          </>
        )}
        {/* Lane visibility — only meaningful once you've drilled into a subgraph. */}
        {!isOverview && !isMatrix && !isData &&
          (["iface", "query", "data"] as Lane[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => toggleLane(l)}
              aria-pressed={!hiddenLanes.has(l)}
              className={`rounded border px-1.5 py-0.5 ${hiddenLanes.has(l) ? "border-border/50 text-muted-foreground/50 line-through" : "border-accent/40 text-accent"}`}
            >
              {LANE_LABEL[l]}
            </button>
          ))}
        {filtering && !isData && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setOnlyProblems(false);
              setHiddenLanes(new Set());
            }}
            className="text-muted-foreground underline hover:text-foreground"
          >
            azzera filtri
          </button>
        )}
        {!isData && (
          <span className="ml-auto flex items-center gap-3">
            <LegendDot color="hsl(var(--accent))" label="legge" />
            <LegendDot color="#f59e0b" label="scrive" />
            <LegendDot color="hsl(var(--muted-foreground))" label="relazione" />
          </span>
        )}
      </div>

      <div className="relative min-h-0 flex-1">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Caricamento mappa…</div>
        ) : all.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            Nessuna tabella o interfaccia ancora — la mappa comparirà appena crei dati e pagine.
          </div>
        ) : isData ? (
          <AppErDiagram appId={appId} canWrite={props.canWrite} />
        ) : isMatrix ? (
          matrix && matrix.rows.length > 0 && matrix.columns.length > 0 ? (
            <MatrixView matrix={matrix} openEntity={openEntity} />
          ) : (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
              {query.trim() || onlyProblems ? "Nessuna riga corrisponde ai filtri." : "Nessuna relazione consumatore→tabella da mostrare."}
            </div>
          )
        ) : nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {filtering
              ? "Nessun nodo corrisponde ai filtri."
              : view.kind === "interface"
                ? "Questa interfaccia non usa ancora query o tabelle."
                : "Niente da mostrare."}
          </div>
        ) : (
          <ReactFlowProvider>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              minZoom={0.2}
              maxZoom={1.6}
              nodesConnectable={false}
              proOptions={{ hideAttribution: true }}
              onNodeClick={(_, n) => {
                if (isOverview) {
                  enterInterface(n.id); // click a container → drill in (animated)
                  return;
                }
                setFocusId((cur) => (cur === n.id ? null : n.id));
              }}
              onPaneClick={() => setFocusId(null)}
            >
              <FitOnView signature={viewKey} />
              <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </ReactFlowProvider>
        )}

        {focused && (
          <div className="absolute right-3 top-3 w-60 rounded-lg border border-border bg-card p-3 shadow-lg">
            <div className="text-xs font-semibold text-foreground">{focused.label}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{focused.sub}</div>
            <div className="mt-2 flex items-center gap-2">
              {openFor(focused) && (
                <button
                  type="button"
                  onClick={openFor(focused)}
                  className="rounded bg-accent px-2 py-1 text-[11px] font-medium text-accent-foreground hover:bg-accent/90"
                >
                  Apri nell'editor →
                </button>
              )}
              <button type="button" onClick={() => setFocusId(null)} className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground">
                Esci dal focus
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Re-runs `fitView` with a transition every time the VIEW changes (drill in/out,
 * open Dati) — this is the "entri con animazione" zoom. Keyed on the view only, so
 * filter/focus tweaks don't jerk the viewport. Must live inside <ReactFlow>.
 */
function FitOnView({ signature }: { signature: string }) {
  const rf = useReactFlow();
  useEffect(() => {
    const t = setTimeout(() => rf.fitView({ duration: 500, padding: 0.2 }), 40);
    return () => clearTimeout(t);
  }, [signature, rf]);
  return null;
}

/**
 * The alternate "who touches what" reading (Slice 5): a consumer×data GRID instead
 * of a graph — rows = tables/datasets, columns = the queries/forms/REST that use
 * them, cells = a read/write dot. No crossing edges, so it stays legible where the
 * graph turns into a hairball. Row/column headers open their editor.
 */
function MatrixView({
  matrix,
  openEntity,
}: {
  matrix: AppMatrix;
  openEntity: (kind: MapNodeKind, entityId: string) => (() => void) | undefined;
}) {
  return (
    <div className="h-full overflow-auto p-3">
      <table className="border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-20 bg-surface px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Tabelle ↓ · Consumatori →
            </th>
            {matrix.columns.map((c) => {
              const Icon = KIND_ICON[c.kind];
              return (
                <th key={c.id} className="sticky top-0 z-10 bg-surface px-1 py-2 align-bottom">
                  <button
                    type="button"
                    onClick={openEntity(c.kind, c.entityId)}
                    title={c.label}
                    className="flex w-[86px] flex-col items-center gap-1 text-[10.5px] text-foreground hover:text-accent"
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    <span
                      className="text-center leading-tight"
                      style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {c.label}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((r) => {
            const Icon = KIND_ICON[r.kind];
            return (
              <tr key={r.id}>
                <th
                  className={`sticky left-0 z-10 whitespace-nowrap bg-card px-3 py-1.5 text-left text-xs font-semibold ${r.orphan ? "text-danger" : "text-foreground"}`}
                >
                  <button type="button" onClick={openEntity(r.kind, r.entityId)} className="flex items-center gap-1.5 hover:text-accent">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {r.label}
                    {r.orphan && (
                      <span className="ml-1 rounded px-1 text-[8px] font-bold text-danger" style={{ border: "1px solid hsl(var(--danger))" }}>
                        ORFANA
                      </span>
                    )}
                  </button>
                </th>
                {r.cells.map((cell, i) => (
                  <td key={matrix.columns[i].id} className="border-t border-border/30 text-center">
                    {cell && (
                      <span
                        title={cell === "write" ? "scrive" : "legge"}
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ background: cell === "write" ? "#f59e0b" : "hsl(var(--accent))" }}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
